import { useMutation, useQuery } from "@apollo/client";
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { Form, useForm } from "@mantine/form";
import { IconPaperclip, IconSend } from "@tabler/icons-react";
import { Collab, UserType } from "../../../../gql/__generated__/graphql";
import { GET_COLLABS_FOR_USER } from "../../../apis/lambda/GraphQL/Collab/queries";
import { SEND_MESSAGE } from "../../../apis/lambda/GraphQL/Messages/mutations";
import { GET_MESSAGES_FOR_COLLAB } from "../../../apis/lambda/GraphQL/Messages/queries";

interface Message {
  collabId: string;
  senderId: string;
  senderType: UserType;
  content: string;
  isRead: boolean;
  createdAt: string;
}

interface MessageModalProps {
  opened: boolean;
  onClose: () => void;
  collab: Collab;
  userType: UserType;
}

function MessageModal({
  opened,
  onClose,
  collab,
  userType: userRole,
}: MessageModalProps) {
  // GraphQL
  const { data, loading, error } = useQuery(GET_MESSAGES_FOR_COLLAB, {
    variables: { collabId: collab.id },
    skip: !opened, // Only fetch when modal is open
  });
  const [sendMessage, { loading: sendingMessage }] = useMutation(SEND_MESSAGE);

  const messages: Message[] = data?.getMessagesForCollab || [];

  const form = useForm({
    initialValues: {
      message: "",
    },
    validate: {
      message: (value: string) =>
        value.trim().length === 0 ? "Message cannot be empty" : null,
    },
  });

  function handleSendMessage(values: { message: string }) {
    void sendMessage({
      variables: {
        content: values.message,
        sendMessageCollabId: collab.id,
      },
      refetchQueries: [
        {
          query: GET_MESSAGES_FOR_COLLAB,
          variables: { collabId: collab.id },
        },
        {
          query: GET_COLLABS_FOR_USER,
        },
      ],
      onCompleted: () => {
        form.reset();
      },
      onError: (error) => {
        console.error("Error sending message:", error);
      },
    });
  }

  function getMessageSenderInfo(message: Message) {
    const isFromCurrentUser =
      (userRole === "BRAND_USER" && message.senderType === "BRAND_USER") ||
      (userRole === "CREATOR" && message.senderType === "CREATOR");

    const senderName =
      message.senderType === "BRAND_USER"
        ? collab.brand?.name || "Brand User"
        : collab.creator?.name || collab.creator?.email || "Creator";

    const senderAvatar =
      message.senderType === "BRAND_USER"
        ? collab.brand?.logo || ""
        : collab.creator?.imageUrl || "";

    return {
      isFromCurrentUser,
      senderName,
      senderAvatar,
    };
  }

  if (!collab) return null;

  return (
    <Modal
      data-component="MessageModal"
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <Text fw={700} size="lg">
            Messages:{" "}
            {collab.products?.map((product) => product.name).join(", ") ||
              "No products"}
          </Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack h={500}>
        <Group gap="xs" mb="xs">
          <Avatar src={collab.brand?.logo} size="sm" radius="xl" />
          <Text size="sm" fw={500}>
            {collab.brand?.name}
          </Text>
          <Text size="sm" c="dimmed">
            and
          </Text>
          <Avatar src={collab.creator?.imageUrl} size="sm" radius="xl" />
          <Text size="sm" fw={500}>
            {collab.creator?.name}
          </Text>
        </Group>

        <Divider />

        <ScrollArea style={{ flex: 1 }} offsetScrollbars>
          <Stack gap="md" p="xs">
            {loading && (
              <Text size="sm" c="dimmed" ta="center">
                Loading messages...
              </Text>
            )}
            {error && (
              <Text size="sm" c="red" ta="center">
                Error loading messages: {error.message}
              </Text>
            )}
            {!loading && !error && messages.length === 0 && (
              <Text size="sm" c="dimmed" ta="center">
                No messages yet. Start the conversation!
              </Text>
            )}
            {messages.map((message, index) => (
              <MessageBubble
                key={`${message.collabId}-${message.senderId}-${message.createdAt}-${index}`}
                message={message}
                senderInfo={getMessageSenderInfo(message)}
              />
            ))}
          </Stack>
        </ScrollArea>

        <Divider />

        <Form form={form} onSubmit={handleSendMessage}>
          <Flex gap="md" align="flex-end">
            <Textarea
              placeholder="Type your message..."
              style={{ flex: 1 }}
              minRows={2}
              maxRows={4}
              autosize
              disabled={sendingMessage}
              {...form.getInputProps("message")}
            />
            <Flex direction="column" gap="xs">
              <ActionIcon
                variant="subtle"
                color="gray"
                title="Attach file (coming soon)"
                disabled
              >
                <IconPaperclip size={20} />
              </ActionIcon>
              <Button
                type="submit"
                                color="primary"
                radius="xl"
                size="md"
                title="Send message"
                loading={sendingMessage}
                disabled={sendingMessage || !form.values.message.trim()}
              >
                <IconSend size={18} />
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Stack>
    </Modal>
  );
}

interface MessageBubbleProps {
  message: Message;
  senderInfo: {
    isFromCurrentUser: boolean;
    senderName: string;
    senderAvatar: string;
  };
}

function MessageBubble({ message, senderInfo }: MessageBubbleProps) {
  const messageDate = new Date(message.createdAt);

  const formattedTime = messageDate.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const formattedDate = messageDate.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });

  return (
    <Flex
      justify={senderInfo.isFromCurrentUser ? "flex-end" : "flex-start"}
      gap="sm"
      align="flex-start"
    >
      {!senderInfo.isFromCurrentUser && (
        <Avatar src={senderInfo.senderAvatar} size="md" radius="xl" />
      )}
      <Box style={{ maxWidth: "75%" }}>
        <Paper
          p="sm"
          radius="md"
                    bg={senderInfo.isFromCurrentUser ? "primary.1" : "gray.1"}
          style={(theme) => ({
            borderBottomLeftRadius: senderInfo.isFromCurrentUser
              ? undefined
              : 0,
            borderBottomRightRadius: senderInfo.isFromCurrentUser
              ? 0
              : undefined,
            borderColor: senderInfo.isFromCurrentUser
              ? theme.colors.primary[3]
              : theme.colors.gray[3],
            borderWidth: 1,
            borderStyle: "solid",
          })}
        >
          {!senderInfo.isFromCurrentUser && (
            <Text size="sm" fw={600} mb={4}>
              {senderInfo.senderName}
            </Text>
          )}
          <Text size="sm">{message.content}</Text>
        </Paper>
        <Text
          size="xs"
          c="dimmed"
          mt={2}
          ta={senderInfo.isFromCurrentUser ? "right" : "left"}
        >
          {formattedDate} at {formattedTime}
        </Text>
      </Box>
      {senderInfo.isFromCurrentUser && (
        <Avatar src={senderInfo.senderAvatar} size="md" radius="xl" />
      )}
    </Flex>
  );
}

export default MessageModal;
